import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Package, Truck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCents = (c) => `€${(c / 100).toFixed(2).replace(".", ",")}`;

const CONFETTI = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  delay: Math.random() * 0.8,
  duration: 2.5 + Math.random() * 2,
  size: 6 + Math.random() * 10,
  color: ["#00FF66", "#00E5FF", "#FF087B"][i % 3],
}));

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          if (res.data.order_id) {
            const orderRes = await axios.get(`${API}/orders/${sessionId}`);
            setOrder(orderRes.data);
          } else if (res.data.plan) {
            setOrder({ plan_name: res.data.plan_name, plan: res.data.plan });
          }
          return;
        }
      } catch (e) {
        console.error(e);
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-[#090D16] text-slate-100 min-h-screen relative overflow-hidden">
      <div className="noise-overlay" />
      {status === "paid" &&
        CONFETTI.map((c) => (
          <motion.span
            key={c.id}
            className="absolute top-[-20px] rounded-full pointer-events-none z-10"
            style={{ left: `${c.x}%`, width: c.size, height: c.size, backgroundColor: c.color }}
            initial={{ y: -30, opacity: 1 }}
            animate={{ y: "110vh", opacity: [1, 1, 0.6] }}
            transition={{ delay: c.delay, duration: c.duration, ease: "easeIn" }}
          />
        ))}

      <header className="border-b border-slate-800 bg-[#090D16]/90 backdrop-blur-xl relative z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <a href="/" data-testid="success-logo-link"><Logo compact /></a>
        </div>
      </header>

      <main className="relative z-20 max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        {status === "checking" && (
          <div data-testid="success-checking" className="flex flex-col items-center gap-5 py-16">
            <Loader2 className="w-12 h-12 text-[#00FF66] animate-spin" />
            <p className="font-display text-4xl uppercase tracking-wide text-white">Betaling controleren...</p>
          </div>
        )}

        {status === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.1 }}
              className="mx-auto w-24 h-24 rounded-full bg-[#00FF66]/15 border-2 border-[#00FF66] flex items-center justify-center glow-lime"
            >
              <CheckCircle2 className="w-12 h-12 text-[#00FF66]" />
            </motion.div>
            <h1 className="mt-8 font-display text-6xl sm:text-7xl uppercase tracking-wide leading-none" data-testid="success-title">
              <span className="text-gradient-splat">Splat!</span> <span className="text-white">Besteld.</span>
            </h1>
            <p className="mt-4 text-slate-400">
              {order?.plan
                ? "Je abonnement is actief! Je eerste drop wordt klaargemaakt."
                : `Bedankt ${order?.customer_name?.split(" ")[0] || "splatkampioen"}! Je betaling is ontvangen en je blasters worden klaargemaakt.`}
            </p>

            {order && order.plan && (
              <div className="mt-10 bg-slate-900/70 border border-[#FF087B]/40 rounded-3xl p-6 sm:p-8 text-left" data-testid="success-subscription-summary">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-brand text-xs uppercase tracking-widest text-slate-500">Abonnement</span>
                  <span className="bg-[#00FF66]/10 text-[#00FF66] border border-[#00FF66]/30 px-3 py-1 rounded-full text-xs font-mono-brand uppercase tracking-widest">
                    Actief
                  </span>
                </div>
                <p className="font-display text-4xl uppercase tracking-wide text-white" data-testid="success-plan-name">
                  {order.plan_name}
                </p>
                <p className="mt-3 text-sm text-slate-400">
                  Elke maand automatisch op de mat. Beheer of zeg op via je accountpagina of mail hallo@gelvaro.nl.
                </p>
              </div>
            )}

            {order && order.items && (
              <div className="mt-10 bg-slate-900/70 border border-slate-800 rounded-3xl p-6 sm:p-8 text-left" data-testid="success-order-summary">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono-brand text-xs uppercase tracking-widest text-slate-500">Ordernummer</span>
                  <span className="font-mono-brand font-bold text-[#00FF66]" data-testid="success-order-id">
                    {order.order_id}
                  </span>
                </div>
                <ul className="space-y-3">
                  {order.items.map((item) => (
                    <li key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-slate-300">
                        {item.quantity}× {item.name}
                      </span>
                      <span className="font-bold text-white">{item.gift ? "Gratis" : formatCents(item.unit_amount * item.quantity)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-slate-800 space-y-2 text-sm">
                  {order.bundle_discount > 0 && (
                    <div className="flex justify-between text-[#00FF66] font-bold">
                      <span>Bundelkorting</span>
                      <span data-testid="success-bundle-discount">-{formatCents(order.bundle_discount)}</span>
                    </div>
                  )}
                  {order.promo_discount > 0 && (
                    <div className="flex justify-between text-[#00FF66] font-bold">
                      <span>Kortingscode {order.promo_code}</span>
                      <span data-testid="success-promo-discount">-{formatCents(order.promo_discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-400">
                    <span>Verzending</span>
                    <span>{order.shipping === 0 ? "Gratis" : formatCents(order.shipping)}</span>
                  </div>
                  <div className="flex justify-between font-extrabold text-white text-base">
                    <span>Totaal betaald</span>
                    <span data-testid="success-total">{formatCents(order.amount_total)}</span>
                  </div>
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 flex items-center gap-3 bg-slate-800/50 rounded-2xl px-4 py-3">
                    <Truck className="w-5 h-5 text-[#00E5FF] shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-white">Verzending naar {order.city}</p>
                      <p className="text-[11px] text-slate-500">Express — binnen 1-2 werkdagen</p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center gap-3 bg-slate-800/50 rounded-2xl px-4 py-3">
                    <Package className="w-5 h-5 text-[#FF087B] shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-white">Track & trace</p>
                      <p className="text-[11px] text-slate-500">Volgt per e-mail naar {order.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              data-testid="success-back-to-shop"
              onClick={() => navigate("/")}
              className="mt-10 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider px-8 py-4 rounded-full glow-lime hover:scale-105 active:scale-95 transition-transform"
            >
              Terug naar de shop
            </button>
          </motion.div>
        )}

        {(status === "pending" || status === "error") && (
          <div data-testid="success-pending" className="py-16">
            <h1 className="font-display text-5xl uppercase tracking-wide text-white">
              {status === "pending" ? "Betaling wordt verwerkt" : "Geen bestelling gevonden"}
            </h1>
            <p className="mt-4 text-slate-400">
              {status === "pending"
                ? "We hebben je betaling nog niet definitief bevestigd gekregen. Check je e-mail voor de bevestiging."
                : "Er is geen geldige sessie meegegeven."}
            </p>
            <button
              data-testid="success-back-to-shop-fallback"
              onClick={() => navigate("/")}
              className="mt-8 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold px-8 py-4 rounded-full transition-colors"
            >
              Terug naar de shop
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
